import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Tag, Divider } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, UserOutlined } from "@ant-design/icons";

const labelStyle = {
    color: "#9ca3af",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 2,
};

const valueStyle = {
    color: "#f9fafb",
    fontSize: 14,
    marginBottom: 12,
    wordBreak: "break-all",
};

const cardStyle = {
    background: "#1f2937",
    border: "1px solid #374151",
    borderRadius: 8,
    padding: "20px 24px",
    marginBottom: 16,
};

const pageStyle = {
    minHeight: "100vh",
    background: "#111827",
    padding: "40px 24px",
    fontFamily: "'Segoe UI', sans-serif",
};

const containerStyle = {
    maxWidth: 820,
    margin: "0 auto",
};

const sectionTitle = {
    color: "#e5e7eb",
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 16,
    borderBottom: "1px solid #374151",
    paddingBottom: 8,
};

export default function MicrosoftSSOResponse() {
    const history = useHistory();
    const [data, setData] = useState(null);

    useEffect(() => {
        try {
            const stored = localStorage.getItem("ssoLoginResult");
            if (stored) setData(JSON.parse(stored));
        } catch (_) {}
    }, []);

    if (!data) {
        return (
            <div style={{ ...pageStyle, textAlign: "center" }}>
                <p style={{ color: "#9ca3af", marginTop: 80 }}>
                    No SSO session data found.{" "}
                    <span
                        style={{ color: "#60a5fa", cursor: "pointer" }}
                        onClick={() => history.push("/")}
                    >
                        Go back to login
                    </span>
                </p>
            </div>
        );
    }

    const { msalAccount, idTokenClaims, backendResult } = data;
    const backend = backendResult?.Obj || {};
    const isSuccess = backendResult?.IsSuccess;
    const userFound = backend?.UserFoundInDatabase;

    return (
        <div style={pageStyle}>
            <div style={containerStyle}>

                {/* ── Header ─────────────────────────────────────────── */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 21 21">
                        <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                        <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                        <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                        <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                    </svg>
                    <div>
                        <h1 style={{ color: "#f9fafb", margin: 0, fontSize: 22, fontWeight: 700 }}>
                            Microsoft SSO — Authentication Response
                        </h1>
                        <p style={{ color: "#6b7280", margin: 0, fontSize: 13 }}>
                            All data received from Microsoft Identity Platform and Levridge backend
                        </p>
                    </div>
                </div>

                {/* ── Status Banner ─────────────────────────────────── */}
                <div style={{
                    ...cardStyle,
                    borderColor: isSuccess ? "#065f46" : "#7f1d1d",
                    background: isSuccess ? "#064e3b" : "#450a0a",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 24,
                }}>
                    {isSuccess
                        ? <CheckCircleOutlined style={{ color: "#34d399", fontSize: 24 }}/>
                        : <CloseCircleOutlined style={{ color: "#f87171", fontSize: 24 }}/>
                    }
                    <div>
                        <div style={{ color: isSuccess ? "#34d399" : "#f87171", fontWeight: 600, fontSize: 15 }}>
                            {isSuccess ? "Backend Validation Successful" : "Backend Validation Failed"}
                        </div>
                        <div style={{ color: "#d1d5db", fontSize: 13, marginTop: 2 }}>
                            {backendResult?.Message}
                        </div>
                    </div>
                    {userFound && (
                        <Tag
                            icon={<UserOutlined />}
                            color="blue"
                            style={{ marginLeft: "auto", fontSize: 13 }}
                        >
                            {backend.UserType} — Role: {backend.Role}
                        </Tag>
                    )}
                </div>

                {/* ── Microsoft Account ─────────────────────────────── */}
                <div style={cardStyle}>
                    <div style={sectionTitle}>Microsoft Account (MSAL)</div>
                    <Row label="Email / UPN" value={msalAccount?.username} />
                    <Row label="Display Name" value={msalAccount?.name} />
                    <Row label="Microsoft Tenant ID" value={msalAccount?.tenantId} />
                    <Row label="Local Account ID" value={msalAccount?.localAccountId} />
                </div>

                {/* ── ID Token Claims ──────────────────────────────── */}
                <div style={cardStyle}>
                    <div style={sectionTitle}>ID Token Claims (from Microsoft)</div>
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <thead>
                                <tr>
                                    <th style={{ ...labelStyle, textAlign: "left", padding: "6px 12px", borderBottom: "1px solid #374151" }}>Claim</th>
                                    <th style={{ ...labelStyle, textAlign: "left", padding: "6px 12px", borderBottom: "1px solid #374151" }}>Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(idTokenClaims || {}).map(([key, val]) => (
                                    <tr key={key} style={{ borderBottom: "1px solid #1f2937" }}>
                                        <td style={{ padding: "6px 12px", color: "#60a5fa", fontFamily: "monospace" }}>{key}</td>
                                        <td style={{ padding: "6px 12px", color: "#e5e7eb", wordBreak: "break-all" }}>
                                            {typeof val === "object" ? JSON.stringify(val) : String(val)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ── Backend Validation Result ─────────────────────── */}
                <div style={cardStyle}>
                    <div style={sectionTitle}>Backend Validation Result (Levridge API)</div>
                    <Row label="Email Extracted" value={backend.Email} />
                    <Row label="Display Name" value={backend.DisplayName} />
                    <Row label="Microsoft Object ID" value={backend.MicrosoftObjectId} />
                    <Row label="Microsoft Tenant ID" value={backend.MicrosoftTenantId} />
                    <Row
                        label="User Found in Database"
                        value={
                            userFound
                                ? <Tag color="green">Yes — {backend.UserType}</Tag>
                                : <Tag color="red">No</Tag>
                        }
                    />
                    {userFound && (
                        <>
                            <Row label="App User ID" value={backend.AppUserId} />
                            <Row label="Assigned Role" value={backend.Role} />
                            <Row label="Role ID" value={backend.RoleId} />
                            <Row label="Application JWT" value={
                                <span style={{ fontFamily: "monospace", fontSize: 11, color: "#86efac" }}>
                                    {backend.AuthToken ? backend.AuthToken.substring(0, 60) + "…" : "—"}
                                </span>
                            }/>
                        </>
                    )}

                    {/* All raw Microsoft claims from backend */}
                    {backend.MicrosoftClaims && Object.keys(backend.MicrosoftClaims).length > 0 && (
                        <>
                            <Divider style={{ borderColor: "#374151", margin: "16px 0 12px" }} />
                            <div style={{ ...labelStyle, marginBottom: 8 }}>All Claims (server-validated)</div>
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                    <tbody>
                                        {Object.entries(backend.MicrosoftClaims).map(([k, v]) => (
                                            <tr key={k} style={{ borderBottom: "1px solid #1f2937" }}>
                                                <td style={{ padding: "5px 10px", color: "#60a5fa", fontFamily: "monospace", whiteSpace: "nowrap" }}>{k}</td>
                                                <td style={{ padding: "5px 10px", color: "#e5e7eb", wordBreak: "break-all" }}>{v}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>

                {/* ── Next Step Notice ─────────────────────────────── */}
                <div style={{
                    ...cardStyle,
                    borderColor: "#1d4ed8",
                    background: "#1e3a5f",
                    textAlign: "center",
                }}>
                    <div style={{ color: "#93c5fd", fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
                        Next Step: Role Configuration
                    </div>
                    <div style={{ color: "#bfdbfe", fontSize: 13 }}>
                        Role assignment for this user will be configured from the SQL database
                        using the email address: <strong style={{ color: "#fff" }}>{backend.Email}</strong>
                    </div>
                </div>

                {/* ── Actions ──────────────────────────────────────── */}
                <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 8 }}>
                    <button
                        onClick={() => history.push("/")}
                        style={{
                            padding: "8px 20px",
                            background: "#374151",
                            color: "#e5e7eb",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                            fontSize: 14,
                        }}
                    >
                        ← Back to Login
                    </button>
                    {userFound && backend.AuthToken && (
                        <button
                            onClick={() => {
                                // Navigate to the correct dashboard based on user type
                                const paths = {
                                    Banker: "/banker-dashboard",
                                    LoanOfficer: "/loanOfficerDashboard",
                                    BankAdmin: "/bankadmindashboard",
                                };
                                history.push(paths[backend.UserType] || "/");
                            }}
                            style={{
                                padding: "8px 20px",
                                background: "#2563eb",
                                color: "#ffffff",
                                border: "none",
                                borderRadius: 6,
                                cursor: "pointer",
                                fontSize: 14,
                                fontWeight: 600,
                            }}
                        >
                            Go to Dashboard →
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div>
            <div style={{ color: "#9ca3af", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>
                {label}
            </div>
            <div style={{ color: "#f9fafb", fontSize: 13, marginBottom: 12, wordBreak: "break-all" }}>
                {value ?? <span style={{ color: "#4b5563" }}>—</span>}
            </div>
        </div>
    );
}
